import { useEffect, useState, useCallback, useMemo } from "react";
import { fetchSystemStatus } from "../api";
import { PIPELINE_SERVICES, SUPPORT_SERVICES, SERVICE_LABELS } from "../features/statistics/constants";

const POLL_INTERVAL = 30000; // 30 seconds

// Core services that must be running for pipeline to function
const CORE_SERVICES = ["merovingian", "sati", "sentinel", "seraph", "operator"];

export default function HealthIndicator({ onNavigate }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const data = await fetchSystemStatus();
      setStatus(data);
      setError(null);
    } catch (e) {
      setError(e.message || "Failed to fetch status");
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [loadStatus]);

  // Calculate health status
  const healthInfo = useMemo(() => {
    if (error || !status?.containers) {
      return { level: "unknown", message: "Status unavailable", degraded: [], down: [] };
    }

    const containers = Array.isArray(status.containers) ? status.containers : [];
    const allServices = [...PIPELINE_SERVICES, ...SUPPORT_SERVICES];

    const down = [];
    const degraded = [];

    for (const service of allServices) {
      const container = containers.find((c) => c.name === service);
      if (!container || container.status !== "running") {
        if (CORE_SERVICES.includes(service)) {
          down.push(service);
        } else {
          degraded.push(service);
        }
      }
    }

    // Check for queue backlog (more than 100 items)
    const queues = status.queues || {};
    for (const [queueName, length] of Object.entries(queues)) {
      if (typeof length === "number" && length > 100) {
        degraded.push(`${queueName} backlog`);
      }
    }

    if (down.length > 0) {
      const names = down.map((s) => SERVICE_LABELS[s] || s).join(", ");
      return {
        level: "critical",
        message: `${down.length} service${down.length > 1 ? "s" : ""} down: ${names}`,
        degraded,
        down,
      };
    }

    if (degraded.length > 0) {
      const names = degraded.map((s) => SERVICE_LABELS[s] || s).join(", ");
      return {
        level: "degraded",
        message: `${degraded.length} degraded: ${names}`,
        degraded,
        down,
      };
    }

    const runningCount = containers.filter((c) => c.status === "running").length;
    return {
      level: "healthy",
      message: `All ${runningCount} services running`,
      degraded: [],
      down: [],
    };
  }, [status, error]);

  const handleClick = () => {
    // Navigate to Statistics page System tab
    if (onNavigate) {
      onNavigate("/statistics?tab=system");
    }
  };

  // Determine visual indicator
  const indicatorClass =
    healthInfo.level === "critical"
      ? "health-critical"
      : healthInfo.level === "degraded"
        ? "health-degraded"
        : healthInfo.level === "healthy"
          ? "health-healthy"
          : "health-unknown";

  // Icon shape changes with status (for accessibility)
  const iconShape =
    healthInfo.level === "critical"
      ? "X"
      : healthInfo.level === "degraded"
        ? "!"
        : healthInfo.level === "healthy"
          ? ""
          : "?";

  return (
    <button
      className={`health-indicator ${indicatorClass}`}
      onClick={handleClick}
      title={healthInfo.message}
      aria-label={`System health: ${healthInfo.message}`}
    >
      <span className="health-dot">
        {iconShape && <span className="health-icon">{iconShape}</span>}
      </span>
    </button>
  );
}
